import React, { useState, useRef, useCallback, useEffect } from 'react';
import caseService from '../../services/caseService';
import inferenceService from '../../services/inferenceService';
import patientService from '../../services/patientService';
import '../../styles/ScanUploadModal.css';

const MAX_FILES = 10;

export default function EditScanModal({ patient, onClose, onComplete }) {
  const [leftFiles, setLeftFiles] = useState(patient.leftFiles || []);
  const [rightFiles, setRightFiles] = useState(patient.rightFiles || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const objectUrlCache = useRef({});

  /* ── Load existing images for completed cases ── */
  useEffect(() => {
    if (patient.status === 'done' && patient.caseId && !patient.leftFiles?.length) {
      setLoading(true);
      caseService.getById(patient.caseId)
        .then(async caseData => {
          if (caseData.images && caseData.images.length > 0) {
            const left = [];
            const right = [];

            for (const img of caseData.images) {
              const fileObj = {
                id: img.id,
                s3_key: img.s3_key,
                side: img.side,
                order: img.order,
                original_filename: img.original_filename,
                isExisting: true,
              };

              // ── Fetch image via authenticated request & create blob URL ──
              try {
                const blob = await caseService.getImageBlob(img.s3_key);
                const previewUrl = URL.createObjectURL(blob);
                fileObj.previewUrl = previewUrl;
                objectUrlCache.current[`existing_${img.id || img.s3_key}`] = previewUrl;
              } catch (err) {
                console.warn('Could not fetch preview for', img.s3_key, err);
              }

              if (img.side === 'left') left.push(fileObj);
              else right.push(fileObj);
            }

            left.sort((a, b) => a.order - b.order);
            right.sort((a, b) => a.order - b.order);
            setLeftFiles(left);
            setRightFiles(right);
          }
        })
        .catch(err => console.error('Failed to load existing images:', err))
        .finally(() => setLoading(false));
    }
  }, [patient.caseId, patient.status, patient.leftFiles]);

  /* ── Cleanup ObjectURLs on unmount ── */
  useEffect(() => {
    const cache = objectUrlCache.current;
    return () => {
      Object.values(cache).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  /* ── Get preview URL for a file ── */
  const getPreviewUrl = useCallback((f) => {
    if (f && typeof f === 'object' && f.isExisting) {
      if (f.previewUrl) return f.previewUrl;
      return caseService.getImageUrl(f.s3_key);
    }
    if (f instanceof File) {
      const key = `${f.name}_${f.size}_${f.lastModified}`;
      if (!objectUrlCache.current[key]) {
        objectUrlCache.current[key] = URL.createObjectURL(f);
      }
      return objectUrlCache.current[key];
    }
    if (typeof f === 'string') {
      return caseService.getImageUrl(f);
    }
    return null;
  }, []);

  /* ── Get display name for a file ── */
  const getFileName = useCallback((f) => {
    if (f instanceof File) return f.name;
    if (f && typeof f === 'object' && f.original_filename) return f.original_filename;
    if (typeof f === 'string') return f.split('/').pop();
    return 'image';
  }, []);

  /* ── Add files ── */
  const addFiles = useCallback((side, incoming) => {
    const setter = side === 'left' ? setLeftFiles : setRightFiles;
    setter(prev => {
      const merged = [...prev];
      for (const f of incoming) {
        if (merged.length >= MAX_FILES) break;
        const isDuplicate = merged.some(x =>
          x instanceof File && f instanceof File && x.name === f.name && x.size === f.size
        );
        if (!isDuplicate) merged.push(f);
      }
      return merged;
    });
  }, []);

  const removeFile = useCallback((side, idx) => {
    if (side === 'left') setLeftFiles(p => p.filter((_, i) => i !== idx));
    else setRightFiles(p => p.filter((_, i) => i !== idx));
  }, []);

  const clearAll = () => { setLeftFiles([]); setRightFiles([]); };

  const handleDrop = useCallback((side, e) => {
    e.preventDefault();
    addFiles(side, Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleBrowse = useCallback((side, e) => {
    const files = Array.from(e.target.files);
    addFiles(side, files);
    e.target.value = '';
  }, [addFiles]);

  /* ── Handle Save / Regenerate ── */
  const handleSave = async () => {
    const allFiles = [...leftFiles, ...rightFiles];
    if (!allFiles.length) {
      setError('Please upload at least one image.');
      return;
    }
    setError('');
    setSaving(true);

    try {
      if (patient.status === 'done' && patient.caseId) {
        const newLeftFiles  = leftFiles.filter(f => f instanceof File);
        const newRightFiles = rightFiles.filter(f => f instanceof File);

        if (!newLeftFiles.length && !newRightFiles.length) {
          setError('Please upload at least one new image to regenerate.');
          setSaving(false);
          return;
        }

        const formData = new FormData();
        newLeftFiles.forEach((f, i)  => formData.append(`left_image_${i + 1}`,  f));
        newRightFiles.forEach((f, i) => formData.append(`right_image_${i + 1}`, f));

        const result = await patientService.updateCaseScan(patient.caseId, formData);

        if (result.status === 'success') {
          // Run analysis — awaited before calling onComplete
          try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            await inferenceService.runAnalysis(patient.caseId);
          } catch (analysisError) {
            console.error('AI analysis failed:', analysisError);
          }

          // Analysis is complete — mark as 'done' so tick shows and View button enables
          onComplete({
            ...patient,
            leftFiles,
            rightFiles,
            status: 'done',            // ← FIXED: was 'processing'
            caseId: patient.caseId,
            analysisComplete: true,    // ← FIXED: was false
          });
        }
      } else {
        onComplete({
          ...patient,
          leftFiles,
          rightFiles,
          status: 'pending',
        });
      }
    } catch (e) {
      console.error('Regenerate failed:', e);
      setError(e.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = patient.status === 'done'
    ? leftFiles.some(f => f instanceof File) || rightFiles.some(f => f instanceof File)
    : leftFiles.length > 0 || rightFiles.length > 0;

  /* ── File Grid Sub-component ── */
  const FileGrid = ({ files, side }) => (
    <div className="sum__file-grid">
      {files.map((f, i) => (
        <div key={`${getFileName(f)}-${i}`} className="sum__thumb-wrap">
          <img
            src={getPreviewUrl(f)}
            alt={getFileName(f)}
            className="sum__thumb"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,' + encodeURIComponent(
                '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="%23eee">' +
                '<rect width="80" height="80" rx="4"/>' +
                '<text x="40" y="45" text-anchor="middle" font-size="10" fill="%23999">No Preview</text></svg>'
              );
            }}
          />
          <button
            className="sum__thumb-remove"
            onClick={e => { e.stopPropagation(); removeFile(side, i); }}
            title="Remove"
          >
            <i className="fa-solid fa-circle-xmark" />
          </button>
        </div>
      ))}
    </div>
  );

  /* ── Drop Zone Sub-component ── */
  const DropZone = ({ side, files, inputRef: ref }) => (
    <div
      className={`sum__dropzone ${files.length ? 'sum__dropzone--has-files' : ''}`}
      onDragOver={e => e.preventDefault()}
      onDrop={e => handleDrop(side, e)}
      onClick={() => !files.length && ref.current?.click()}
    >
      <input
        ref={ref}
        type="file"
        accept="image/bmp"
        multiple
        className="d-none"
        onChange={e => handleBrowse(side, e)}
      />
      {files.length === 0 ? (
        <div className="text-center text-muted py-3">
          <i className="fa-solid fa-cloud-arrow-up fs-3 mb-2 d-block sum__upload-icon" />
          <span className="small sum__upload-text">
            Drag and drop or{' '}
            <span className="sum__browse-link" onClick={e => { e.stopPropagation(); ref.current?.click(); }}>
              browse
            </span>
          </span>
          <div className="small text-muted mt-1 sum__upload-subtext">BMP files only</div>
        </div>
      ) : (
        <>
          <FileGrid files={files} side={side} />
          {files.length < MAX_FILES && (
            <div className="sum__add-more" onClick={e => { e.stopPropagation(); ref.current?.click(); }}>
              <i className="fa-solid fa-plus me-1" />Add more
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="sum__overlay">
      <div className="sum__modal-container">
        <div className="sum__bg" />
        <div className="sum__bg-tint" />
        <div className="sum__content">

          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold mb-0 sum__title">
              <i className="fa-solid fa-pen me-2" />Edit Scan
            </h5>
            <button className="sum__close-btn" onClick={onClose} title="Close">X</button>
          </div>

          <div className="sum__patient-banner mb-3">
            <span>Patient ID: <strong>{patient.id}</strong></span>
            <span>Patient Name: <strong>{patient.name || '—'}</strong></span>
          </div>

          {patient.status === 'done' && (
            <div className="sum__regen-notice mb-3">
              <i className="fa-solid fa-rotate me-1" />
              Saving will <strong>regenerate</strong> the analysis and PDF report. Old files will be deleted.
            </div>
          )}

          {loading && (
            <div className="sum__loading-notice mb-3">
              <i className="fa-solid fa-spinner fa-spin me-2" />
              Loading existing images...
            </div>
          )}

          <p className="fw-bold mb-3 sum__title">Upload a scan image*</p>

          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <DropZone side="right" files={rightFiles} inputRef={rightRef} />
              <p className="text-center fw-semibold mt-2 sum__subtitle">Right Breast Image</p>
            </div>
            <div className="col-12 col-md-6">
              <DropZone side="left" files={leftFiles} inputRef={leftRef} />
              <p className="text-center fw-semibold mt-2 sum__subtitle">Left Breast Image</p>
            </div>
          </div>

          {error && (
            <div className="sum__error mb-2">
              <i className="fa-solid fa-triangle-exclamation me-1" />{error}
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button className="btn sum__btn-clear px-4" onClick={clearAll} disabled={saving}>
              Clear
            </button>
            <button className="btn sum__btn-start px-4" onClick={handleSave} disabled={!canSave || saving}>
              {saving ? (
                <><i className="fa-solid fa-spinner fa-spin me-2" />Regenerating…</>
              ) : (
                'Regenerate Report'
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
